/* Customer portal — Contact dispatch: message form + history. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Mail, Phone, Clock } from "lucide-react";

const statusStyle = {
  open:    "bg-amber-100 text-amber-700 border-amber-300",
  replied: "bg-emerald-100 text-emerald-700 border-emerald-300",
  closed:  "bg-neutral-200 text-neutral-500 border-neutral-300",
};

export default function PortalContact() {
  const [msgs, setMsgs] = useState([]);
  const [form, setForm] = useState({ subject: "", body: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/portal/messages"); setMsgs(r.data); } catch (e) { console.warn("portal messages load failed", e); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.body.trim()) return toast.error("Subject and message required");
    setBusy(true);
    try {
      await api.post("/portal/messages", form);
      toast.success("Message sent — dispatch will reply shortly");
      setForm({ subject: "", body: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="portal-contact">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-neutral-500">Contact</div>
        <h1 className="text-4xl font-black">Contact dispatch</h1>
        <p className="text-neutral-500 text-sm mt-1">Send a message and we'll come back within business hours.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <form onSubmit={submit} className="bg-white border border-neutral-200 p-6 space-y-4 shadow-sm">
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Subject</Label>
            <Input data-testid="contact-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-none border-neutral-300" />
          </div>
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Message</Label>
            <Textarea data-testid="contact-body" required rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-none border-neutral-300" />
          </div>
          <div className="flex justify-end">
            <Button data-testid="contact-submit" type="submit" disabled={busy} className="rounded-none bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-sm font-bold">
              <Send className="w-4 h-4 mr-2" /> {busy ? "Sending…" : "Send message"}
            </Button>
          </div>
        </form>

        <aside className="bg-[#0a0a0b] text-white p-6 space-y-4">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-neutral-400">Dispatch office</div>
            <div className="text-lg font-black mt-1">Mon–Fri · 6am–5pm</div>
            <div className="text-sm text-neutral-400">Sat · 6am–12pm</div>
          </div>
          <div className="pt-4 border-t border-neutral-800 space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4 text-primary-orange" /> <b>0499 505 249</b>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4 text-primary-orange" /> dispatch@gadtech.com
            </div>
            <div className="flex items-center gap-2 text-sm text-neutral-400">
              <Clock className="w-4 h-4" /> After-hours emergencies: leave a voicemail
            </div>
          </div>
        </aside>
      </div>

      <div>
        <h2 className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">Message history</h2>
        <div className="space-y-3">
          {msgs.length === 0 && (
            <div className="bg-white border border-neutral-200 p-10 text-center text-neutral-500">No messages yet.</div>
          )}
          {msgs.map((m) => (
            <div key={m.id} className="bg-white border border-neutral-200 p-5" data-testid={`portal-message-${m.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="font-bold">{m.subject}</div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${statusStyle[m.status] || ""}`}>{m.status}</span>
              </div>
              <div className="text-xs text-neutral-500 mono mt-1">Sent {(m.created_at || "").slice(0, 16).replace("T", " ")}</div>
              <div className="text-sm text-neutral-800 mt-3 whitespace-pre-wrap">{m.body}</div>
              {m.reply && (
                <div className="mt-4 pt-4 border-t border-neutral-100 bg-primary-orange/5 -mx-5 -mb-5 px-5 py-4">
                  <div className="text-[10px] uppercase tracking-widest text-primary-orange mb-1">Dispatch replied</div>
                  <div className="text-sm text-neutral-800 whitespace-pre-wrap">{m.reply}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
